import React from 'react'
import { Mountain, History, Award, Globe } from 'lucide-react'

interface AboutSectionProps {
  language?: 'zh' | 'en'
}

const AboutSection: React.FC<AboutSectionProps> = ({ language = 'en' }) => {
  const features = [
    {
      icon: <Mountain className="w-8 h-8" />,
      title: {
        zh: '自然奇观',
        en: 'Natural Wonder'
      },
      description: {
        zh: '72峰、36岩、24涧，构成壮丽的自然景观',
        en: '72 peaks, 36 cliffs, 24 streams form magnificent natural landscape'
      }
    },
    {
      icon: <History className="w-8 h-8" />,
      title: {
        zh: '千年历史',
        en: 'Millennium History'
      },
      description: {
        zh: '自唐代起就是道教圣地，拥有1400多年历史',
        en: 'Taoist holy land since Tang Dynasty, over 1400 years of history'
      }
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: {
        zh: '世界遗产',
        en: 'World Heritage'
      },
      description: {
        zh: '1994年被联合国教科文组织列为世界文化遗产',
        en: 'Listed as UNESCO World Cultural Heritage in 1994'
      }
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: {
        zh: '全球影响',
        en: 'Global Influence'
      },
      description: {
        zh: '武当武术和文化影响遍及全球100多个国家',
        en: 'Wudang martial arts and culture influence over 100 countries worldwide'
      }
    }
  ]

  return (
    <section id="about" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="section-title gradient-text">
            {language === 'zh' ? '关于武当山' : 'About Wudang Mountain'}
          </h2>
          <p className="text-lg text-wudang-gray max-w-3xl mx-auto mb-8 leading-relaxed chinese-text">
            {language === 'zh'
              ? '武当山位于中国湖北省西北部，是中国道教的发源地和武当武术的诞生地。这里不仅是自然与人文完美结合的典范，更是世界文化遗产的瑰宝。'
              : 'Located in northwestern Hubei Province, China, Wudang Mountain is the birthplace of Chinese Taoism and Wudang martial arts. It is not only a perfect example of the integration of nature and culture, but also a treasure of world cultural heritage.'}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {features.map((feature, index) => (
            <div key={index} className="card text-center hover:scale-105 transition-transform duration-300">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-wudang-blue/10 to-wudang-green/10 flex items-center justify-center text-wudang-gold">
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold text-wudang-blue mb-3">
                {language === 'zh' ? feature.title.zh : feature.title.en}
              </h3>
              <p className="text-wudang-gray">
                {language === 'zh' ? feature.description.zh : feature.description.en}
              </p>
            </div>
          ))}
        </div>

        {/* Detailed Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Image */}
          <div className="relative">
            <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1200"
                alt={language === 'zh' ? '武当山全景' : 'Wudang Mountain Panorama'}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-gradient-to-br from-wudang-gold to-wudang-green rounded-2xl -z-10 opacity-20" />
          </div>

          {/* Right Column - Text */}
          <div>
            <h3 className="text-2xl font-bold text-wudang-blue mb-6">
              {language === 'zh' ? '为什么选择武当山？' : 'Why Choose Wudang Mountain?'}
            </h3>
            
            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-semibold text-wudang-green mb-2">
                  {language === 'zh' ? '文化深度' : 'Cultural Depth'}
                </h4>
                <p className="text-wudang-gray leading-relaxed chinese-text">
                  {language === 'zh'
                    ? '武当山是中国道教文化的活化石，保存着完整的道教建筑群和丰富的文化遗产。这里的每一座宫殿、每一尊神像都承载着千年的历史记忆。'
                    : 'Wudang Mountain is a living fossil of Chinese Taoist culture, preserving complete Taoist architectural complexes and rich cultural heritage. Every palace and every statue here carries a thousand years of historical memory.'}
                </p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-wudang-green mb-2">
                  {language === 'zh' ? '武术传承' : 'Martial Arts Heritage'}
                </h4>
                <p className="text-wudang-gray leading-relaxed chinese-text">
                  {language === 'zh'
                    ? '作为内家武术的发源地，武当山孕育了太极拳、八卦掌、形意拳等著名拳种。这里不仅是武术爱好者的朝圣地，也是养生文化的实践地。'
                    : 'As the birthplace of internal martial arts, Wudang Mountain gave birth to famous boxing styles such as Tai Chi, Bagua Zhang, and Xingyi Quan. It is not only a pilgrimage site for martial arts enthusiasts, but also a practice ground for health preservation culture.'}
                </p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-wudang-green mb-2">
                  {language === 'zh' ? '自然和谐' : 'Natural Harmony'}
                </h4>
                <p className="text-wudang-gray leading-relaxed chinese-text">
                  {language === 'zh'
                    ? '武当山的建筑与自然环境完美融合，体现了"天人合一"的道家思想。这里的山水不仅美丽，更蕴含着深厚的哲学意义。'
                    : 'The architecture of Wudang Mountain perfectly integrates with the natural environment, embodying the Taoist concept of "harmony between heaven and man." The mountains and waters here are not only beautiful but also contain profound philosophical meaning.'}
                </p>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t border-wudang-gray/20">
              <p className="text-wudang-gray italic chinese-text">
                {language === 'zh'
                  ? '"武当山不仅是一座山，更是一部活着的历史，一种流动的文化，一种永恒的精神。"'
                  : '"Wudang Mountain is not just a mountain, but a living history, a flowing culture, and an eternal spirit."'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default AboutSection